import React, { useEffect, useState, useRef } from "react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Save, Upload, Building2 } from "lucide-react";
import { toast } from "sonner";

const Settings = () => {
  const [form, setForm] = useState({ name: "", nif: "", phone: "", email: "", logo_path: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/company");
        setForm({ ...form, ...data });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update("logo_path", data.path);
      toast.success("Logo enviado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha no upload");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings/company", form);
      toast.success("Configurações salvas");
    } catch (e) {
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-zinc-500">Carregando...</div>;

  const inp = "w-full px-3 py-2.5 bg-[#09090b] border border-zinc-800 rounded-lg text-zinc-100 placeholder-zinc-600 focus:border-zinc-500 focus:outline-none text-sm transition-all";

  return (
    <div className="max-w-3xl mx-auto fade-up" data-testid="settings-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold mb-2">Configurações</div>
        <h1 className="font-display text-4xl font-black text-zinc-50 tracking-tighter">Dados da Empresa</h1>
        <p className="text-zinc-500 mt-2 text-sm">Estas informações aparecerão no cabeçalho e rodapé de todos os PDFs gerados.</p>
      </div>

      <form onSubmit={save} className="space-y-6">
        <section className="rounded-xl border border-zinc-800 bg-[#121214] p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-4">Logo da Empresa</div>
          <div className="flex items-center gap-5">
            <div className="w-32 h-32 rounded-lg border border-zinc-800 bg-[#0e0e10] overflow-hidden flex items-center justify-center">
              {form.logo_path ? (
                <img src={fileUrl(form.logo_path)} alt="" className="w-full h-full object-contain" />
              ) : (
                <Building2 className="w-8 h-8 text-zinc-700" strokeWidth={1.5} />
              )}
            </div>
            <div>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="company-logo-upload-btn"
                className="flex items-center gap-2 px-4 py-2.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-200 rounded-lg text-sm font-medium transition-all"
              >
                <Upload className="w-4 h-4" strokeWidth={1.5} /> Enviar Logo
              </button>
              <div className="text-xs text-zinc-500 mt-2">PNG ou JPG. Recomendado: 600×200px.</div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-zinc-800 bg-[#121214] p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-4">Dados de Contacto</div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1.5">Nome</label>
              <input value={form.name} onChange={(e) => update("name", e.target.value)} className={inp} data-testid="company-name-input" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1.5">NIF</label>
              <input value={form.nif} onChange={(e) => update("nif", e.target.value)} className={inp} data-testid="company-nif-input" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1.5">Telefone</label>
              <input value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inp} data-testid="company-phone-input" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1.5">E-mail</label>
              <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inp} data-testid="company-email-input" />
            </div>
          </div>
        </section>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            data-testid="settings-save-btn"
            className="flex items-center gap-2 px-5 py-3 bg-zinc-100 hover:bg-white text-zinc-900 font-bold rounded-lg text-sm transition-all disabled:opacity-60"
          >
            <Save className="w-4 h-4" strokeWidth={2} /> {saving ? "Salvando..." : "Salvar Configurações"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Settings;
